import { useCallback } from 'react'
import { useObservableValue, useSubject } from '@/hooks/rx-hooks'
import { scan } from 'rxjs/operators'
import { CustomSelectState } from '@/components/inputs/CustomSelect'
import {
  type CustomSelectEvent,
  CustomSelectStateObservable,
  type ItemSource,
} from './custom-select-state-observable'

interface UseCustomSelectStateFromItemSourceProps<V> {
  /**
   * The item source to use. NOTE: Should be a stable reference.
   */
  itemSource: ItemSource<V>
  /**
   * The debounce time in ms to use when searching (default = none).
   */
  debounce?: number
}

/**
 * Uses the `ItemSource` which is a simple approach to
 * getting a custom select up and running without having to manage
 * a bunch of state.
 */
export function useCustomSelectStateFromItemSource<V>({
  itemSource,
  debounce = 0,
}: UseCustomSelectStateFromItemSourceProps<V>) {
  const input$ = useSubject<string>()
  const load$ = useSubject<unknown>()
  const onLoadMore = useCallback(() => load$.next(undefined), [load$])
  // The input value.
  const inputValue = useObservableValue(input$, { initial: '' })
  const onInputValueChange = useCallback(
    (value: string) => input$.next(value),
    [input$],
  )
  const { isLoading, isEoS, searchResults } = useObservableValue<
    CustomSelectState<V>
  >(
    useCallback(
      () =>
        CustomSelectStateObservable(input$, load$, itemSource, debounce).pipe(
          scan(reducer, CustomSelectState.initial),
        ),
      [input$, load$, itemSource, debounce],
    ),
    { initial: CustomSelectState.initial },
  )

  return {
    onLoadMore,
    inputValue,
    onInputValueChange,
    isLoading,
    isEoS,
    searchResults,
  }
}

/**
 * State reducer for the `ItemSource`-based hook.
 */
function reducer<V>(state: CustomSelectState<V>, event: CustomSelectEvent<V>) {
  switch (event.type) {
    case 'ResetItems':
      return {
        ...state,
        isEoS: false,
        searchResults: [],
      }
    case 'Loading':
      return {
        ...state,
        isEoS: false,
        isLoading: true,
      }
    case 'Loaded':
      return {
        ...state,
        isLoading: false,
        searchResults: [...state.searchResults, ...event.items],
      }
    case 'EndOfStream':
      return {
        ...state,
        isLoading: false,
        isEoS: true,
      }
  }
  return state
}
